'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from '@/components/ui/Button';
import { PRESET_MESSAGES, MAX_MESSAGE_LENGTH } from '@/lib/gift-constants';

export interface GiftCardFormData {
  recipientName: string;
  recipientEmail: string;
  senderEmail: string;
  message: string;
}

const schema = yup.object({
  recipientName: yup.string().required('El nombre del destinatario es obligatorio'),
  recipientEmail: yup
    .string()
    .email('Email inválido')
    .required('El email del destinatario es obligatorio'),
  senderEmail: yup.string().email('Email inválido').required('Tu email es obligatorio'),
  message: yup.string().max(MAX_MESSAGE_LENGTH).defined(),
});

interface GiftCardFormProps {
  tierName: string;
  displayPrice: string;
  onSubmit: (data: GiftCardFormData) => void;
  isSubmitting: boolean;
  onSuccess?: () => void;
}

export default function GiftCardForm({
  tierName,
  displayPrice,
  onSubmit,
  isSubmitting,
  onSuccess,
}: GiftCardFormProps) {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<GiftCardFormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      recipientName: '',
      recipientEmail: '',
      senderEmail: '',
      message: '',
    },
  });

  const message = watch('message');

  function handlePresetClick(preset: string) {
    if (selectedPreset === preset) {
      setSelectedPreset(null);
      setValue('message', '');
    } else {
      setSelectedPreset(preset);
      setValue('message', preset);
    }
  }

  function handleMessageChange(value: string) {
    setValue('message', value);
    setSelectedPreset(null);
  }

  function handleFormSubmit(data: GiftCardFormData) {
    onSubmit(data);
    onSuccess?.();
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Tier badge */}
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#e6f7f6] text-sm font-semibold text-[#00A19B]">
        {tierName} &mdash; {displayPrice}
      </div>

      {/* Recipient section */}
      <div>
        <h3 className="text-sm font-bold text-[#373A36] uppercase tracking-wider mb-4">
          ¿Para quién es?
        </h3>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="recipientName"
              className="block text-sm font-medium text-[#373A36] mb-1.5 after:content-['_*'] after:text-red-500 after:font-bold"
            >
              Nombre del destinatario
            </label>
            <input
              id="recipientName"
              type="text"
              {...register('recipientName')}
              className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow"
              placeholder="Nombre de quien recibe el regalo"
            />
            {errors.recipientName && (
              <p className="mt-1 text-xs text-red-500">{errors.recipientName.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="recipientEmail"
              className="block text-sm font-medium text-[#373A36] mb-1.5 after:content-['_*'] after:text-red-500 after:font-bold"
            >
              Email del destinatario
            </label>
            <input
              id="recipientEmail"
              type="email"
              {...register('recipientEmail')}
              className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow"
              placeholder="email@ejemplo.com"
            />
            {errors.recipientEmail && (
              <p className="mt-1 text-xs text-red-500">{errors.recipientEmail.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="senderEmail"
              className="block text-sm font-medium text-[#373A36] mb-1.5 after:content-['_*'] after:text-red-500 after:font-bold"
            >
              Tu email
            </label>
            <input
              id="senderEmail"
              type="email"
              {...register('senderEmail')}
              className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow"
              placeholder="tu@email.com"
            />
            {errors.senderEmail && (
              <p className="mt-1 text-xs text-red-500">{errors.senderEmail.message}</p>
            )}
            <p className="mt-1 text-xs text-[#6b6e68]">
              Te enviaremos el comprobante de compra a este email.
            </p>
          </div>
        </div>
      </div>

      {/* Message section */}
      <div>
        <h3 className="text-sm font-bold text-[#373A36] uppercase tracking-wider mb-4">
          Mensaje personal{' '}
          <span className="font-normal text-[#6b6e68] normal-case">(opcional)</span>
        </h3>

        {/* Preset chips */}
        <div className="flex flex-wrap gap-2 mb-3">
          {PRESET_MESSAGES.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePresetClick(preset)}
              aria-pressed={selectedPreset === preset}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                selectedPreset === preset
                  ? 'bg-[#00A19B] text-white'
                  : 'bg-[#FAFAF8] text-[#6b6e68] border border-[#E8E8E4] hover:border-[#00A19B] hover:text-[#00A19B]'
              }`}
            >
              {preset}
            </button>
          ))}
        </div>

        <label htmlFor="giftMessage" className="sr-only">
          Mensaje personal
        </label>
        <div className="relative">
          <textarea
            id="giftMessage"
            {...register('message')}
            onChange={(e) => handleMessageChange(e.target.value)}
            maxLength={MAX_MESSAGE_LENGTH}
            rows={3}
            className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E8E8E4] rounded-2xl text-[#373A36] text-sm focus:outline-none focus:ring-2 focus:ring-[#00A19B] focus:border-transparent transition-shadow resize-none"
            placeholder="Escribe un mensaje personalizado..."
          />
          <span className="absolute bottom-3 right-4 text-xs text-[#6b6e68]">
            {(message ?? '').length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
      </div>

      {/* Submit */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        isLoading={isSubmitting}
        disabled={!isValid || isSubmitting}
        className="w-full"
      >
        Agregar al carrito
      </Button>
    </form>
  );
}
